import React, { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
} from "react-router-dom";
import SnackbarProvider from "react-simple-snackbar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getCompany } from "./actions/companyActions";
import lngs from "./lngs";
import NavBar from "./components/NavBar/NavBar";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import RTL from "./utils/stylesprovider";

// Lazy loading components
const Home = React.lazy(() => import("./components/Home/Home"));
const Invoice = React.lazy(() => import("./components/Invoice/Invoice"));
const Invoices = React.lazy(() => import("./components/Invoices/Invoices"));
const InvoiceDetails = React.lazy(() =>
  import("./components/InvoiceDetails/InvoiceDetails")
);
const ClientList = React.lazy(() => import("./components/Clients/ClientList"));
const Login = React.lazy(() => import("./components/Login/Login"));
const Dashboard = React.lazy(() => import("./components/Dashboard/Dashboard"));
const Settings = React.lazy(() => import("./components/Settings/Settings"));
const SettingsHome = React.lazy(() =>
  import("./components/Settings/SettingsHome")
);
const ProfileSettings = React.lazy(() =>
  import("./components/Profile/Settings")
);
const Forgot = React.lazy(() => import("./components/Password/Forgot"));
const Reset = React.lazy(() => import("./components/Password/Reset"));
const AddCategory = React.lazy(() =>
  import("./components/Categories/AddCategory")
);
const Categories = React.lazy(() =>
  import("./components/Categories/Categories")
);
const Items = React.lazy(() => import("./components/Items/Items"));
const BarcodeSettings = React.lazy(() =>
  import("./components/BarcodeSettings/BarcodeSettings")
);
const POS = React.lazy(() => import("./components/POS/pos"));
const Receipt = React.lazy(() => import("./components/POS/receipt"));
const Register = React.lazy(() => import("./components/Login/Register"));
const Prices = React.lazy(() => import("./components/Prices/Prices"));
const ContactUs = React.lazy(() => import("./components/Contactus/Contactus"));
const Download = React.lazy(() => import("./components/Download/Download"));
const ProfitCentersList = React.lazy(() =>
  import("./components/ProfitCenters/ProfitCentersList")
);
const Companies = React.lazy(() => import("./components/Companies/Companies"));
const UserGroups = React.lazy(() =>
  import("./components/UserGroups/UserGroups")
);
const AddUserGroup = React.lazy(() =>
  import("./components/UserGroups/AddUserGroup")
);
const Users = React.lazy(() => import("./components/Users/Users"));
const AddUser = React.lazy(() => import("./components/Users/AddUser"));
const RentalList = React.lazy(() => import("./components/Rental/RentalList"));
const RentalForm = React.lazy(() => import("./components/Rental/RentalForm"));
const RentalHome = React.lazy(() => import("./components/Rental/RentalHome"));
const BuildingsList = React.lazy(() =>
  import("./components/Building/BuildingsList")
);
const BuildingDetails = React.lazy(() =>
  import("./components/Building/BuildingDetails")
);

const Accounts = React.lazy(() => import("./components/Accounts/Accounts"));
const Transaction = React.lazy(() =>
  import("./components/Transaction/Transaction")
);
const Transactions = React.lazy(() =>
  import("./components/Transaction/Transactions")
);

const TaxSettings = React.lazy(() =>
  import("./components/TaxSettings/TaxSettings")
);

const AccountingHome = React.lazy(() =>
  import("./components/Home/AccountingHome")
);

const Letter = React.lazy(() => import("./components/Letters/Letter"));
const LettersHome = React.lazy(() =>
  import("./components/Letters/LettersHome")
);

const LetterViewer = React.lazy(() =>
  import("./components/Letters/LetterViewer")
);

const LettersList = React.lazy(() =>
  import("./components/Letters/LettersList")
);
const Costcenters = React.lazy(() =>
  import("./components/Costcenters/Costcenters")
);

const TrialBalance = React.lazy(() =>
  import("./components/TrialBalance/TrialBalance")
);

const BalanceSheet = React.lazy(() =>
  import("./components/BalanceSheet/BalanceSheet")
);

const IncomeStatement = React.lazy(() =>
  import("./components/IncomeStatement/IncomeStatement")
);

const PurchaseInvoice = React.lazy(() =>
  import("./components/Purchase/Purchase")
);

const PurchaseInvoiceDetails = React.lazy(() =>
  import("./components/Purchase/PurchaseDetails")
);
const PurchaseInvoices = React.lazy(() =>
  import("./components/Purchase/PurchaseInvoices")
);

const GeneratePayroll = React.lazy(() =>
  import("./components/Payrolls/GeneratePayroll")
);
const PayrollList = React.lazy(() =>
  import("./components/Payrolls/PayrollList")
);
const PropertyManagement = React.lazy(() =>
  import("./components/PropertyManagement/PropertyManagement")
);

const AssetsComponent = React.lazy(() =>
  import("./components/Assets/AssetsComponent")
);
const AssetsList = React.lazy(() => import("./components/Assets/AssetsList"));

const AttendanceList = React.lazy(() =>
  import("./components/Attendance/AttendanceList")
);

const RecordAttendance = React.lazy(() =>
  import("./components/Attendance/RecordAttendance")
);
const AdminSettings = React.lazy(() =>
  import("./components/AdminSettings/AdminSettings")
);

const SMSProvider = React.lazy(() =>
  import("./components/SMSPoviders/SMSProvider")
);
const SMS = React.lazy(() => import("./components/SMS/Sms"));
const BarcodePrint = React.lazy(() =>
  import("./components/BarcodePrint/BarcodePrint")
);

const AppHomePage = React.lazy(() =>
  import("./components/AppHomePage/AppHomePage")
);

const ProjectsList = React.lazy(() =>
  import("./components/Projects/ProjectsList")
);
const AccountingSettings = React.lazy(() =>
  import("./components/AccountingSettings/AccountingSettings")
);

const NotificationsList = React.lazy(() =>
  import("./components/Notifications/NotificationsList")
);

const Vouchers = React.lazy(() => import("./components/Vouchers/vouchers"));

const Coupons = React.lazy(() => import("./components/coupons/Coupons"));

const CreateCoupons = React.lazy(() =>
  import("./components/coupons/CreateCoupons")
);

const ProductPackages = React.lazy(() =>
  import("./components/ProductPackages/Packages")
);

const Features = React.lazy(() => import("./components/Features/Features"));
const Properties = React.lazy(() => import("./components/Properties/AddProperty"));

// Clinic Management Components
const ClinicManagementHome = React.lazy(() =>
  import("./components/ClinicManagement/ClinicManagementHome")
);
const DoctorList = React.lazy(() =>
  import("./components/ClinicManagement/Doctor/DoctorList")
);
const DoctorDetail = React.lazy(() =>
  import("./components/ClinicManagement/Doctor/DoctorDetail")
);
const DoctorForm = React.lazy(() =>
  import("./components/ClinicManagement/Doctor/DoctorForm")
);
const PatientList = React.lazy(() =>
  import("./components/ClinicManagement/Patient/PatientList")
);
const PatientDetail = React.lazy(() =>
  import("./components/ClinicManagement/Patient/PatientDetail")
);
const PatientForm = React.lazy(() =>
  import("./components/ClinicManagement/Patient/PatientForm")
);
const AppointmentList = React.lazy(() =>
  import("./components/ClinicManagement/Appointment/AppointmentList")
);
const AppointmentDetail = React.lazy(() =>
  import("./components/ClinicManagement/Appointment/AppointmentDetail")
);
const AppointmentForm = React.lazy(() =>
  import("./components/ClinicManagement/Appointment/AppointmentForm")
);
const LabResultList = React.lazy(() =>
  import("./components/ClinicManagement/LabResult/LabResultList")
);
const LabResultDetail = React.lazy(() =>
  import("./components/ClinicManagement/LabResult/LabResultDetail")
);
const LabResultForm = React.lazy(() =>
  import("./components/ClinicManagement/LabResult/LabResultForm")
);
const LabTestList = React.lazy(() =>
  import("./components/ClinicManagement/LabTest/LabTestList")
);
const LabTestDetail = React.lazy(() =>
  import("./components/ClinicManagement/LabTest/LabTestDetail")
);
const LabTestForm = React.lazy(() =>
  import("./components/ClinicManagement/LabTest/LabTestForm")
);

const MedicalRecordList = React.lazy(() =>
  import("./components/ClinicManagement/MedicalRecord/MedicalRecordList")
);
const MedicalRecordDetail = React.lazy(() =>
  import("./components/ClinicManagement/MedicalRecord/MedicalRecordDetail")
);
const MedicalRecordForm = React.lazy(() =>
  import("./components/ClinicManagement/MedicalRecord/MedicalRecordForm")
);

const PrescriptionList = React.lazy(() =>
  import("./components/ClinicManagement/Prescription/PrescriptionList")
);
const PrescriptionDetail = React.lazy(() =>
  import("./components/ClinicManagement/Prescription/PrescriptionDetail")
);
const PrescriptionForm = React.lazy(() =>
  import("./components/ClinicManagement/Prescription/PrescriptionForm")
);

const MedicationList = React.lazy(() =>
  import("./components/ClinicManagement/Medication/MedicationList")
);
const MedicationDetail = React.lazy(() =>
  import("./components/ClinicManagement/Medication/MedicationDetail")
);
const MedicationForm = React.lazy(() =>
  import("./components/ClinicManagement/Medication/MedicationForm")
);

const Messaging = React.lazy(() =>
  import("./components/Messaging/Messaging")
);

function App() {
  const { i18n } = useTranslation();
  const user = JSON.parse(localStorage.getItem("profile"));
  const currentLang = i18n.resolvedLanguage;
  const lang = window.location.pathname.split("/")[1] || "en";
  const isInApp = window.location.pathname.includes("/app/");
  const [openDrower, setOpenDrower] = useState(false);
  const { company } = useSelector((state) => state.companies);
  const dispatch = useDispatch();
  const rentalLetterTypes = [
    "lease_extension",
    "eviction_notice",

    "sublease_agreement",
    "property_disclosure",
    "foreclosure_notice",
    "mortgage_statement",
    "homeowner_association_letter",
    "move_in_notice",
    "move_out_notice",
    "security_deposit_return",
    "purchase_agreement",
    "closing_disclosure",
    "real_estate_offer",
    "real_estate_inspection_report",
    "title_insurance_policy",
    "deed_of_trust",
    "mortgage_pre_approval_letter",
  ];
  useEffect(() => {
    if (currentLang !== lang && lngs[lang]?.nativeName?.length > 0) {
      i18n.changeLanguage(lang);
    }
    if (user?.result?.companyId) {
      dispatch(getCompany(user.result.companyId));
    }
  }, []);

  const isLanguageRTL = lngs[i18n.resolvedLanguage]?.isRtl || false;

  useEffect(() => {
    const rootElement = document.documentElement;
    rootElement.setAttribute("dir", isLanguageRTL ? "rtl" : "ltr");
  }, [isLanguageRTL]);

  return (
    <RTL isRTL={isLanguageRTL}>
      <div
        dir={isLanguageRTL ? "rtl" : "ltr"}
        className={
          isInApp
            ? isLanguageRTL
              ? openDrower
                ? "marginRTLBig"
                : "marginRTL"
              : openDrower
                ? "marginLTRBig"
                : "marginLTR"
            : ""
        }
      >
        <Router>
          <SnackbarProvider>
            {user && <NavBar openDrower={openDrower} />}
            <Header
              openDrower={openDrower}
              toggleOpenDrower={() => setOpenDrower(!openDrower)}
            />
            <Suspense fallback={<div>Loading...</div>}>
              <Switch>
                <Route path="/:lang?/" exact component={Home} />
                <Route path="/:lang/login" exact component={Login} />
                <Route path="/:lang/app/main" exact component={AppHomePage} />
                <Route
                  path="/:lang/app/dashboard"
                  exact
                  component={Dashboard}
                />
                <Route
                  path="/:lang/app/settings"
                  exact
                  component={SettingsHome}
                />
                <Route
                  path="/:lang/app/properties/add-property"
                  exact
                  component={Properties}
                />
                <Route
                  path="/:lang/app/settings/general"
                  exact
                  component={Settings}
                />
                <Route
                  path="/:lang/app/settings/tax-settings"
                  exact
                  component={TaxSettings}
                />
                <Route
                  path="/:lang/app/profile"
                  exact
                  component={ProfileSettings}
                />
                <Route
                  path="/:lang/app/property-management"
                  exact
                  component={PropertyManagement}
                />
                <Route path="/:lang/forgot" exact component={Forgot} />
                <Route path="/:lang/reset/:token" exact component={Reset} />
                <Route path="/:lang/prices" exact component={Prices} />
                <Route path="/:lang/contactus" exact component={ContactUs} />
                <Route path="/:lang/download" exact component={Download} />
                <Route
                  path="/:lang/app/add/invoice"
                  exact
                  component={Invoice}
                />
                <Route
                  path="/:lang/app/edit/invoice/:id"
                  exact
                  component={Invoice}
                />
                <Route
                  path="/:lang/app/duplicate/invoice/:id"
                  exact
                  component={Invoice}
                />
                <Route
                  path="/:lang/app/invoice/:id"
                  exact
                  component={InvoiceDetails}
                />
                <Route
                  path="/:lang/app/accounting-settings"
                  exact
                  component={AccountingSettings}
                />
                <Route path="/:lang/app/invoices" exact component={Invoices} />
                <Route
                  path="/:lang/app/invoices/byRental/:rentalId"
                  exact
                  component={Invoices}
                />
                <Route path="/:lang/app/pos" exact component={POS} />
                <Route path="/:lang/app/pos/:id" exact component={Receipt} />
                <Route path="/:lang/signup" exact component={Register} />
                <Route
                  path="/:lang/app/customers"
                  exact
                  component={ClientList}
                />
                <Route
                  path="/:lang/app/categories/add"
                  exact
                  component={AddCategory}
                />
                <Route
                  path="/:lang/app/categories"
                  exact
                  component={Categories}
                />
                <Route path="/:lang/app/items" exact component={Items} />
                <Route
                  path="/:lang/app/barcode-settings"
                  exact
                  component={BarcodeSettings}
                />
                <Route
                  path="/:lang/app/admin-settings"
                  exact
                  component={AdminSettings}
                />
                <Route
                  path="/:lang/app/admin-settings/companies"
                  exact
                  component={Companies}
                />
                <Route
                  path="/:lang/app/add/group/"
                  exact
                  component={AddUserGroup}
                />
                <Route
                  path="/:lang/app/edit/group/:id"
                  exact
                  component={AddUserGroup}
                />
                <Route path="/:lang/app/groups" exact component={UserGroups} />
                <Route path="/:lang/app/add/user/" exact component={AddUser} />
                <Route
                  path="/:lang/app/edit/user/:id"
                  exact
                  component={AddUser}
                />
                <Route path="/:lang/app/users" exact component={Users} />
                <Route
                  path="/:lang/app/rentals/unpaid"
                  exact
                  render={() => <RentalList unpaid={true} />}
                />
                <Route path="/:lang/app/rentals" exact component={RentalList} />
                <Route
                  path="/:lang/app/rentals/home"
                  exact
                  component={RentalHome}
                />
                <Route
                  path="/:lang/app/rentals/add"
                  exact
                  component={RentalForm}
                />
                <Route
                  path="/:lang/app/rentals/edit/:id"
                  exact
                  component={RentalForm}
                />

                <Route
                  path="/:lang/app/rentals/doc/:letterType"
                  exact
                  render={({ match }) => (
                    <LettersList letterType={match.params.letterType} />
                  )}
                />
                <Route
                  path="/:lang/app/rentals/doc/:letterType/add"
                  exact
                  render={({ match }) => (
                    <Letter letterType={match.params.letterType} />
                  )}
                />

                <Route
                  path="/:lang/app/rentals/doc/:letterType/edit/:id"
                  exact
                  render={({ match }) => (
                    <Letter letterType={match.params.letterType} />
                  )}
                />

                <Route
                  path="/:lang/app/letters/letter/add"
                  exact
                  render={({ match }) => (
                    <Letter letterType={match.params.letterType} />
                  )}
                />

                <Route
                  path="/:lang/app/rentals/doc/:letterType/view/:id"
                  exact
                  render={({ match }) => (
                    <LetterViewer letterType={match.params.letterType} />
                  )}
                />

                <Route
                  path="/:lang/app/costcenters"
                  exact
                  component={Costcenters}
                />
                <Route
                  path="/:lang/app/buildings"
                  exact
                  component={BuildingsList}
                />
                <Route
                  path="/:lang/app/buildings/:id/details"
                  exact
                  component={BuildingDetails}
                />
                <Route path="/:lang/app/Accounts" exact component={Accounts} />
                <Route
                  path="/:lang/app/transaction"
                  exact
                  component={Transaction}
                />
                <Route
                  path="/:lang/app/transaction/edit/:id"
                  exact
                  component={Transaction}
                />
                <Route
                  path="/:lang/app/transactions"
                  exact
                  component={Transactions}
                />
                <Route
                  path="/:lang/app/accounting"
                  exact
                  component={AccountingHome}
                />
                <Route
                  path="/:lang/app/projects"
                  exact
                  component={ProjectsList}
                />
                <Route
                  path="/:lang/app/notifications"
                  exact
                  component={NotificationsList}
                />
                <Route path="/:lang/app/letters/add" exact component={Letter} />
                <Route
                  path="/:lang/app/letters/edit/:id"
                  exact
                  component={Letter}
                />
                <Route
                  path="/:lang/app/letters/view/:id"
                  exact
                  component={LetterViewer}
                />
                <Route
                  path="/:lang/app/letters/home"
                  exact
                  component={LettersHome}
                />
                <Route
                  path="/:lang/app/letters"
                  exact
                  component={LettersList}
                />
                <Route
                  path="/:lang/app/trial-balance"
                  exact
                  component={TrialBalance}
                />
                <Route
                  path="/:lang/app/balance-sheet"
                  exact
                  component={BalanceSheet}
                />
                <Route
                  path="/:lang/app/label/print/:id"
                  exact
                  component={BarcodePrint}
                />
                <Route
                  path="/:lang/app/income-statement"
                  exact
                  component={IncomeStatement}
                />
                <Route
                  path="/:lang/app/purchases/add"
                  exact
                  component={PurchaseInvoice}
                />
                <Route
                  path="/:lang/app/purchases/edit/:id"
                  exact
                  component={PurchaseInvoice}
                />
                <Route
                  path="/:lang/app/purchases/duplicate/:id"
                  exact
                  component={PurchaseInvoice}
                />
                <Route
                  path="/:lang/app/purchases/view/:id"
                  exact
                  component={PurchaseInvoiceDetails}
                />
                <Route
                  path="/:lang/app/purchases"
                  exact
                  component={PurchaseInvoices}
                />
                <Route
                  path="/:lang/app/payrolls"
                  exact
                  component={PayrollList}
                />
                <Route
                  path="/:lang/app/assets"
                  exact
                  component={AssetsComponent}
                />
                <Route
                  path="/:lang/app/assets/add-asset"
                  exact
                  component={AssetsList}
                />
                <Route
                  path="/:lang/app/payrolls/generate"
                  exact
                  component={GeneratePayroll}
                />
                <Route
                  path="/:lang/app/attendances"
                  exact
                  component={AttendanceList}
                />
                <Route
                  path="/:lang/app/attendances/record"
                  exact
                  component={RecordAttendance}
                />
                <Route
                  path="/:lang/app/profit-centers"
                  exact
                  component={ProfitCentersList}
                />
                <Route
                  path="/:lang/app/sms-providers"
                  exact
                  component={SMSProvider}
                />
                <Route path="/:lang/app/coupons" exact component={Coupons} />
                <Route
                  path="/:lang/app/coupons/print/:id"
                  exact
                  component={CreateCoupons}
                />
                <Route path="/:lang/app/sms-send" exact component={SMS} />

                <Route path="/:lang/app/vouchers" exact component={Vouchers} />
                <Route
                  path="/:lang/app/admin-settings/packages"
                  exact
                  component={ProductPackages}
                />
                <Route
                  path="/:lang/app/admin-settings/features"
                  exact
                  component={Features}
                />
                <Route
                  path="/:lang/app/accounting-settings"
                  exact
                  component={AccountingSettings}
                />
                <Route
                  path="/:lang/app/clinic"
                  exact
                  component={ClinicManagementHome}
                />
                <Route
                  path="/:lang/app/clinic/doctors"
                  exact
                  component={DoctorList}
                />
                <Route
                  path="/:lang/app/clinic/doctors/add"
                  exact
                  component={DoctorForm}
                />
                <Route
                  path="/:lang/app/clinic/doctors/edit/:id"
                  exact
                  component={DoctorForm}
                />
                <Route
                  path="/:lang/app/clinic/doctors/:id"
                  exact
                  component={DoctorDetail}
                />
                <Route
                  path="/:lang/app/clinic/patients"
                  exact
                  component={PatientList}
                />
                <Route
                  path="/:lang/app/clinic/patients/add"
                  exact
                  component={PatientForm}
                />
                <Route
                  path="/:lang/app/clinic/patients/edit/:id"
                  exact
                  component={PatientForm}
                />
                <Route
                  path="/:lang/app/clinic/patients/:id"
                  exact
                  component={PatientDetail}
                />
                <Route
                  path="/:lang/app/clinic/appointments"
                  exact
                  component={AppointmentList}
                />
                <Route
                  path="/:lang/app/clinic/appointments/add"
                  exact
                  component={AppointmentForm}
                />
                <Route
                  path="/:lang/app/clinic/appointments/edit/:id"
                  exact
                  component={AppointmentForm}
                />
                <Route
                  path="/:lang/app/clinic/appointments/:id"
                  exact
                  component={AppointmentDetail}
                />
                <Route
                  path="/:lang/app/clinic/lab-results"
                  exact
                  component={LabResultList}
                />
                <Route
                  path="/:lang/app/clinic/lab-results/:id"
                  exact
                  component={LabResultDetail}
                />
                <Route
                  path="/:lang/app/clinic/lab-results/add"
                  exact
                  component={LabResultForm}
                />
                <Route
                  path="/:lang/app/clinic/lab-results/edit/:id"
                  exact
                  component={LabResultForm}
                />
                <Route
                  path="/:lang/app/clinic/lab-tests"
                  exact
                  component={LabTestList}
                />
                <Route
                  path="/:lang/app/clinic/lab-tests/add"
                  exact
                  component={LabTestForm}
                />
                <Route
                  path="/:lang/app/clinic/lab-tests/edit/:id"
                  exact
                  component={LabTestForm}
                />
                <Route
                  path="/:lang/app/clinic/lab-tests/:id"
                  exact
                  component={LabTestDetail}
                />
                <Route
                  path="/:lang/app/clinic/medical-records"
                  exact
                  component={MedicalRecordList}
                />
                <Route
                  path="/:lang/app/clinic/medical-records/add"
                  exact
                  component={MedicalRecordForm}
                />
                <Route
                  path="/:lang/app/clinic/medical-records/edit/:id"
                  exact
                  component={MedicalRecordForm}
                />
                <Route
                  path="/:lang/app/clinic/medical-records/:id"
                  exact
                  component={MedicalRecordDetail}
                />
                <Route
                  path="/:lang/app/clinic/prescriptions"
                  exact
                  component={PrescriptionList}
                />
                <Route
                  path="/:lang/app/clinic/prescriptions/add"
                  exact
                  component={PrescriptionForm}
                />
                <Route
                  path="/:lang/app/clinic/prescriptions/edit/:id"
                  exact
                  component={PrescriptionForm}
                />
                <Route
                  path="/:lang/app/clinic/prescriptions/:id"
                  exact
                  component={PrescriptionDetail}
                />
                <Route
                  path="/:lang/app/clinic/medications"
                  exact
                  component={MedicationList}
                />
                <Route
                  path="/:lang/app/clinic/medications/add"
                  exact
                  component={MedicationForm}
                />
                <Route
                  path="/:lang/app/clinic/medications/edit/:id"
                  exact
                  component={MedicationForm}
                />
                <Route
                  path="/:lang/app/clinic/medications/:id"
                  exact
                  component={MedicationDetail}
                />
                <Route
                  path="/:lang/app/messaging"
                  exact
                  component={Messaging}
                />
              </Switch>
            </Suspense>

            <Footer />
          </SnackbarProvider>
        </Router>
      </div>
    </RTL>
  );
}

export default App;
